/**
 * @file 通用函数
 * @author 小白
 * @export getRsaKey 获取RSA公钥key
 * @export getRsa 获取RSA加密后的数据
 * @export queryParams 获取Object转query参数
 */
import JSEncrypt from "jsencrypt";
import { getRasKeyFn } from "@/api/common.js";
import router from "@/router/index.js";
import { getUserInfo, getStudentInfo } from "@/api/user.js";
import dayjs from "dayjs";
import test from "./test.js";
// import { useStore } from "vuex";
// const store = useStore();
// console.log("========", store);

/**
 * @description 获取RSA的公钥Key
 */
export const getRsaKey = async () => {
  const key = await getRasKeyFn();
  localStorage.setItem("rsakey", key.vx);
};

/**
 * @description 获取RSA加密数据
 * @param {string} value 被加密参数
 */
export const getRsa = async (value) => {
  const encryptor = new JSEncrypt(); // 创建加密对象实例
  // 之前ssl生成的公钥，复制的时候要小心不要有空格
  const pubKey = localStorage.getItem("rsakey");
  encryptor.setPublicKey(pubKey); // 设置公钥
  return encryptor.encrypt(value); // 对内容进行加密
};

/**
 * @description 对象转url参数
 * @param {object} data,对象
 * @param {Boolean} isPrefix,是否自动加上"?"
 * @param {string} arrayFormat 规则 indices|brackets|repeat|comma
 */
export const queryParams = (
  data = {},
  isPrefix = true,
  arrayFormat = "brackets"
) => {
  const prefix = isPrefix ? "?" : "";
  const _result = [];
  let commaStr = "";

  if (["indices", "brackets", "repeat", "comma"].indexOf(arrayFormat) == -1)
    arrayFormat = "brackets";
  for (const key in data) {
    const value = data[key];
    // 去掉为空的参数
    if (["", undefined, null].indexOf(value) >= 0) {
      continue;
    }
    // 如果值为数组，另行处理
    if (value.constructor === Array) {
      // e.g. {ids: [1, 2, 3]}
      switch (arrayFormat) {
        case "indices":
          // 结果: ids[0]=1&ids[1]=2&ids[2]=3
          for (let i = 0; i < value.length; i++) {
            _result.push(`${key}[${i}]=${value[i]}`);
          }
          break;
        case "brackets":
          // 结果: ids[]=1&ids[]=2&ids[]=3
          value.forEach((_value) => {
            _result.push(`${key}[]=${_value}`);
          });
          break;
        case "repeat":
          // 结果: ids=1&ids=2&ids=3
          value.forEach((_value) => {
            _result.push(`${key}=${_value}`);
          });
          break;
        case "comma":
          // 结果: ids=1,2,3
          value.forEach((_value) => {
            commaStr += (commaStr ? "," : "") + _value;
          });
          _result.push(`${key}=${commaStr}`);
          break;
        default:
          value.forEach((_value) => {
            _result.push(`${key}[]=${_value}`);
          });
      }
    } else {
      _result.push(`${key}=${value}`);
    }
  }
  return _result.length ? prefix + _result.join("&") : "";
};

/**
 * @description 退出登录
 * @param {Boolean} isTimeOut 是否需要倒计时退出登录（默认不需要）
 * @param {Number} time 退出倒计时（默认2000）
 */
export const loginOut = async (isTimeOut = false, time = 2000) => {
  let path = router.currentRoute.value.fullPath;
  let bool = ["home", "notice", "myPage"].some((item) => path.includes(item));
  let toPath = bool ? "/home" : "/login";

  if (bool) {
    localStorage.removeItem("access_token2");
    localStorage.removeItem("userInfo2");
  } else {
    localStorage.removeItem("access_token");
    localStorage.removeItem("userInfo");
  }

  if (isTimeOut) {
    setTimeout(() => {
      // router.replace("/login");
      // router.replace("/");
      router.replace(toPath);
    }, time);
  } else {
    // router.replace("/login");
    // router.replace("/");
    router.replace(toPath);
  }
};

/**
 * @description 时间戳转日期
 * @param {String} ts 需要该规则的字符串
 */
export const tsConvertDate = (ts, format = "YYYY年MM月DD日") => {
  return dayjs(ts).format(format);
};

/**
 * @description 时间戳转时间
 * @param {String} ts 需要该规则的字符串
 */
export const tsConvertTime = (ts, format = "YYYY-MM-DD HH:mm:ss") => {
  return dayjs(ts).format(format);
};

/**
 * @description 秒转时分秒
 * @param {String} seconds 秒数
 * @param {String} formatType 转换类型 en英文 cn中文 data
 */
export const secondsConverTime = (seconds, formatType = "en") => {
  let second = parseInt(seconds);
  let minute = 0;
  let hour = 0;
  if (second > 60) {
    minute = parseInt(second / 60);
    second = parseInt(second % 60);
    if (minute > 60) {
      hour = parseInt(minute / 60);
      minute = parseInt(minute % 60);
    }
  }
  const s = second < 10 ? "0" + second : second;
  const m = minute < 10 ? "0" + minute : minute;
  const h = hour < 10 ? "0" + hour : hour;

  let result = "";
  if (formatType === "cn") {
    result = "" + s + "秒";
    if (minute > 0) {
      result = "" + m + "分" + result;
    }
    if (hour > 0) {
      result = "" + h + "时" + result;
    }
  } else if (formatType === "en") {
    result = `${h}:${m}:${s}`;
  } else if (formatType === "data") {
    result = {
      h,
      m,
      s,
    };
  }

  return result;
};

/**
 * @description 深度克隆
 * @param {object} obj 需要深度克隆的对象
 * @returns {*} 克隆后的对象或者原值（不是对象）
 */
export const deepClone = (obj) => {
  // 对常见的“非”值，直接返回原来值
  if ([null, undefined, NaN, false].includes(obj)) return obj;
  if (typeof obj !== "object" && typeof obj !== "function") {
    // 原始类型直接返回
    return obj;
  }
  const o = test.array(obj) ? [] : {};
  for (const i in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, i)) {
      o[i] = typeof obj[i] === "object" ? deepClone(obj[i]) : obj[i];
    }
  }
  return o;
};

/**
 * @description 判断基本类型是否为空
 * @param {any} value 被判断的值
 **/
export const isEmptyValue = (value) => {
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return true;
  }
  return false;
};

/**
 * xss攻击
 */
export const handleStrXss = (str) => {
  return str
    .replace(/&lt;script/g, "&amp;lt;script")
    .replace(/script&gt;/g, "script&amp;gt;")
    .replace(/&lt;script.*&gt;.*&lt;\/script.*&gt;/g, "")
    .replace(
      /on(error|mousewheel|mouseover|click|load|onload|submit|focus|blur|start)=[^"]*/g,
      ""
    );
};

export const clearHtmlTag = (str) => {
  if (!str) return "";
  return str
    .replace(/<img[^>]*>/gi, "[图片]")
    .replace(/<iframe[^>]*>/, "[视频]")
    .replace(/&nbsp;/gi, "")
    .replace(/<[^>]+>/g, "");
};

// url转换对象
export const searchParamToObj = (url = location.href) =>
  Object.fromEntries(new URL(url).searchParams);

/**
 * @description 获取时间的时间戳
 * @param {String} date antDesignVue的组件日期的时间戳
 */
export const getDateTs = (date) => {
  if (!date) return "";
  return {
    start: date[0].valueOf(),
    end: date[1].valueOf(),
  };
};

/**
 * 生成uuid
 */
export const createUuid = () => {
  let s = [];
  let hexDigits = "0123456789abcdef";
  for (let i = 0; i < 32; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
  s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
  s[8] = s[13] = s[18] = s[23];
  //带横杠的
  // s[8] = s[13] = s[18] = s[23] = '-';
  let uuid = s.join("");
  return uuid;
};

// 更新用户信息
export const updateUserInfo = () => {
  return new Promise((resolve, reject) => {
    getUserInfo()
      .then((res) => {
        localStorage.setItem("userInfo", JSON.stringify(res));
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// 更新学员用户信息
export const updateStudentInfo = () => {
  return new Promise((resolve, reject) => {
    getStudentInfo()
      .then((res) => {
        localStorage.setItem("userInfo2", JSON.stringify(res));
        // store.commit('data/SET_USER_INFO2', userInfo)
        // console.log("getUserInfo===", store.getters["data/getUserInfo2"]);
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// 数字转中文
export const changeNumToHan = (num) => {
  let AA = new Array(
    "零",
    "一",
    "二",
    "三",
    "四",
    "五",
    "六",
    "七",
    "八",
    "九",
    "十"
  );
  let BB = new Array("", "十", "百", "仟", "萬", "億", "点", "");
  let a = ("" + num).replace(/(^0*)/g, "").split("."),
    k = 0,
    re = "";
  for (let i = a[0].length - 1; i >= 0; i--) {
    switch (k) {
      case 0:
        re = BB[7] + re;
        break;
      case 4:
        if (!new RegExp("0{4}//d{" + (a[0].length - i - 1) + "}$").test(a[0]))
          re = BB[4] + re;
        break;
      case 8:
        re = BB[5] + re;
        BB[7] = BB[5];
        k = 0;
        break;
    }
    if (k % 4 == 2 && a[0].charAt(i + 2) != 0 && a[0].charAt(i + 1) == 0)
      re = AA[0] + re;
    if (a[0].charAt(i) != 0) re = AA[a[0].charAt(i)] + BB[k % 4] + re;
    k++;
  }
  // if (a.length > 1) {
  //   // 加上小数部分(如果有小数部分)
  //   re += BB[6];
  //   for (let j = 0; i < a[1].length; j++) re += AA[a[1].charAt(j)];
  // }
  if (re == "一十") re = "十";
  if (re.match(/^一/) && re.length == 3) re = re.replace("一", "");
  return re;
};

/**
 * @param {Number} len uuid的长度
 * @param {Boolean} firstU 将返回的首字母置为"u"
 * @param {Nubmer} radix 生成uuid的基数(意味着返回的字符串都是这个基数),2-二进制,8-八进制,10-十进制,16-十六进制
 */
export const getGuId = (len = 32, firstU = true, radix = null) => {
  const chars =
    "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz".split("");
  const uuid = [];
  radix = radix || chars.length;

  if (len) {
    // 如果指定uuid长度,只是取随机的字符,0|x为位运算,能去掉x的小数位,返回整数位
    for (let i = 0; i < len; i++) uuid[i] = chars[0 | (Math.random() * radix)];
  } else {
    let r;
    // rfc4122标准要求返回的uuid中,某些位为固定的字符
    uuid[8] = uuid[13] = uuid[18] = uuid[23] = "-";
    uuid[14] = "4";

    for (let i = 0; i < 36; i++) {
      if (!uuid[i]) {
        r = 0 | (Math.random() * 16);
        uuid[i] = chars[i == 19 ? (r & 0x3) | 0x8 : r];
      }
    }
  }
  // 移除第一个字符,并用u替代,因为第一个字符为数值时,该guuid不能用作id或者class
  if (firstU) {
    uuid.shift();
    return `u${uuid.join("")}`;
  }
  return uuid.join("");
};

// 生成uuid
// 通过时间戳和随机数生成
export const generateUUID = () => {
  let timestamp = new Date().getTime();
  const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      const r = (timestamp + Math.random() * 16) % 16 | 0;
      timestamp = Math.floor(timestamp / 16);
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
};

//根据身份证提取性别和出生年月
export const extractBirthAndGender = (idCard) => {
  if (!idCard || idCard.length !== 18) {
    throw new Error("身份证号无效");
  }

  // 提取出生日期
  const birthYear = idCard.substring(6, 10);
  const birthMonth = idCard.substring(10, 12);
  const birthDay = idCard.substring(12, 14);
  const birthDate = `${birthYear}-${birthMonth}-${birthDay}`;

  // 提取性别
  const genderCode = parseInt(idCard.charAt(16), 10);
  const gender = genderCode % 2 === 1 ? 1 : 2;

  // 使用 dayjs 格式化出生日期
  const formattedBirthDate = dayjs(birthDate).valueOf();

  return {
    birthDate: formattedBirthDate,
    gender: gender,
  };
};

//获取文件名称和文件扩展
export const extractBirthAndGenderFromFileName = (file) => {
  const fileName = file.name;
  const dotIndex = fileName.lastIndexOf(".");
  let nameWithoutExtension = fileName;
  let fileExtension = "";

  if (dotIndex !== -1) {
    nameWithoutExtension = fileName.substring(0, dotIndex);
    fileExtension = fileName.substring(dotIndex + 1);
  }
  
  return {
    nameWithoutExtension,
    fileExtension,
  };
};
